import { ShelvingInterface } from "../../useShelvingStore";
import { Color, TireRackItemI } from "../../types/store";
import { useStoreSystem } from "../../useStoreSystem";
import {
    BracketElement,
    BracketItem,
    BracketsItem,
    ItemGroupI,
    ItemI,
    PartInterface,
    SectionItemI,
} from "../../../shared";
import { SectionInterface, StoreWallInterface, Sides } from "../../types/store";
import { useStoreRoom } from "../../useStoreRoom";
import { ConstructionSection } from "../../../controllers/types";
import { updateShelfRodIds } from "../../helpers/updateShelfRodIds";

export const updateItemsColors = (
    colorsItems: Color,
    shelving: ShelvingInterface,
    tireRackColor: Color = colorsItems
): { shelving: ShelvingInterface } => {
    const itemsGroup: ItemGroupI[] = useStoreSystem.getState().itemsGroup;
    const currentConstructions: ConstructionSection[] = Object.values(useStoreSystem.getState().constructions || {});

    const byOfferId = new Map<string, ItemI>();
    const byOffersIds = new Map<string, ItemI>();

    const tireRacks: TireRackItemI[] = useStoreRoom.getState().tireRacks;

    for (const group of itemsGroup) {
        for (const el of group.ELEMENTS) {
            if (el.BITRIX_OFFER_ID) byOfferId.set(String(el.BITRIX_OFFER_ID), el);
            if (Array.isArray(el.BITRIX_OFFERS_IDS)) {
                for (const id of el.BITRIX_OFFERS_IDS) {
                    if (id) byOffersIds.set(String(id), el);
                }
            }
        }
    }

    const findSrc = (originalId?: string) => {
        if (!originalId) return undefined;
        const key = String(originalId);
        return byOfferId.get(key) ?? byOffersIds.get(key);
    };

    const pickNomenkl = (originalId?: string) => {
        const src = findSrc(originalId);
        if (!src || !Array.isArray(src.newNomenkl)) return undefined;
        // @ts-ignore
        return src.newNomenkl.find((n) => n.color === colorsItems);
    };

    type HasOffer = { bitrixId: string; bitrixIdOriginal?: string; price: number; type?: string[] };
    const swap = <T extends HasOffer>(obj: T): T => {
        if (obj?.type?.includes("Штанга")) {
            return { ...obj };
        }

        const nm = pickNomenkl(obj.bitrixIdOriginal || obj.bitrixId);

        if (!nm?.bitrixId) return obj;

        return {
            ...obj,
            bitrixId: nm.bitrixId,
            price: nm.priceNomenkl ?? obj.price,
        };
    };

    const updateBracketItem = (b: BracketItem): BracketItem => ({
        ...b,
        elements: b.elements.map((e: BracketElement) => swap(e)),
    });

    const updateBrackets = (b: BracketsItem): BracketsItem => ({
        ...b,
        left: updateBracketItem(b.left),
        right: updateBracketItem(b.right),
    });

    const updatePartsArr = (arr?: PartInterface[]) => (Array.isArray(arr) ? arr.map((p) => swap(p)) : arr);

    const updateSectionItem = (item: SectionItemI): SectionItemI => {
        const selfUpdated = swap(item);
        return {
            ...selfUpdated,
            brackets: updateBrackets(item.brackets),
            amps: item.amps ? updateBrackets(item.amps) : undefined,
            edges: updatePartsArr(item.edges),
            parts: updatePartsArr(item.parts) ?? [],
        };
    };

    const updateSection = (section: SectionInterface): SectionInterface => {
        return {
            ...section,
            items: section.items.map(updateSectionItem),
        };
    };

    const updatedShelving = (Object.entries(shelving) as [Sides, StoreWallInterface][]).reduce((acc, [side, wall]) => {
        acc[side] = {
            ...wall,
            sections: updateShelfRodIds(wall.sections, colorsItems).map(updateSection),
        };
        return acc;
    }, {} as ShelvingInterface);

    (() => {
        if (!Array.isArray(tireRacks) || tireRacks.length === 0) return;

        const allConstrElements = currentConstructions.flatMap((c) => c?.ELEMENTS || []);

        const byConstrOfferId = new Map<string, any>();
        const byConstrOffersIds = new Map<string, any>();
        for (const el of allConstrElements) {
            if (el?.BITRIX_OFFER_ID != null) byConstrOfferId.set(String(el.BITRIX_OFFER_ID), el);
            if (Array.isArray(el?.BITRIX_OFFERS_IDS)) {
                for (const id of el.BITRIX_OFFERS_IDS) if (id) byConstrOffersIds.set(String(id), el);
            }
        }

        const findConstr = (originalId?: number | string) => {
            if (originalId == null) return undefined;
            const key = String(originalId);
            return byConstrOfferId.get(key) ?? byConstrOffersIds.get(key);
        };

        const pickNm = (el?: any) => {
            if (!el?.newNomenkl?.length) return undefined;
            const byColor = el.newNomenkl.find((n: any) => n.color === tireRackColor);
            return byColor ?? el.newNomenkl[0];
        };

        const updatedTireRacks = tireRacks.map((tr) => {
            const src = findConstr(tr.bitrixId);
            const nm = pickNm(src);

            let nextBitrixId = tr.bitrixId;
            if (nm?.bitrixId != null) {
                const asNum = Number(nm.bitrixId);
                if (!Number.isNaN(asNum)) nextBitrixId = asNum;
            } else if (src?.BITRIX_OFFER_ID != null) {
                nextBitrixId = Number(src.BITRIX_OFFER_ID);
            }

            const nextPrice =
                (nm?.priceNomenkl as number | undefined) ??
                (src?.PRICE as number | undefined) ??
                tr.pricing?.total_cost ??
                0;

            return {
                ...tr,
                parts: [],
                bitrixId: nextBitrixId,
                pricing: {
                    base_price: nextPrice,
                    shelf_price: nextPrice,
                    total_cost: nextPrice,
                },
            };
        });

        useStoreRoom.setState({ tireRacks: updatedTireRacks });
    })();

    return { shelving: updatedShelving };
};
